import { addStudiesToFolder, createStudyFolder, deleteStudyFolder, getFoldersForStudy, getStudyFolders, removeStudyFromFolder, reorderFolder, updateStudyFolder, } from '@/lib/api/folders';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
function buildFolderTree(folders) {
    const folderMap = new Map();
    // Create nodes with empty children arrays
    for (const folder of folders) {
        folderMap.set(folder.id, { ...folder, children: [] });
    }
    const personal = [];
    const clinic = [];
    // Build tree structure
    for (const folder of folders) {
        const node = folderMap.get(folder.id);
        if (folder.parentFolderId && folderMap.has(folder.parentFolderId)) {
            folderMap.get(folder.parentFolderId).children.push(node);
        }
        else {
            // Root level folder
            if (folder.scope === 'USER') {
                personal.push(node);
            }
            else {
                clinic.push(node);
            }
        }
    }
    // Sort children by displayOrder
    const sortByOrder = (a, b) => a.displayOrder - b.displayOrder;
    const sortTree = (nodes) => {
        nodes.sort(sortByOrder);
        for (const node of nodes) {
            sortTree(node.children);
        }
    };
    sortTree(personal);
    sortTree(clinic);
    return { personal, clinic };
}
export function useFolderTree() {
    return useQuery({
        queryKey: ['studyFolders'],
        queryFn: async () => {
            const folders = await getStudyFolders();
            return buildFolderTree(folders);
        },
        staleTime: 30000,
    });
}
export function useCreateFolder() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (request) => createStudyFolder(request),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['studyFolders'] });
        },
    });
}
export function useUpdateFolder() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, request }) => updateStudyFolder(id, request),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['studyFolders'] });
        },
    });
}
export function useDeleteFolder() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id) => deleteStudyFolder(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['studyFolders'] });
        },
    });
}
export function useReorderFolder() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, request }) => reorderFolder(id, request),
        onMutate: async ({ id, request }) => {
            // Cancel outgoing refetches
            await queryClient.cancelQueries({ queryKey: ['studyFolders'] });
            // Snapshot previous value
            const previousFolders = queryClient.getQueryData(['studyFolders']);
            // Optimistically update
            if (previousFolders) {
                const updated = previousFolders.map((folder) => folder.id === id ? { ...folder, displayOrder: request.newDisplayOrder } : folder);
                queryClient.setQueryData(['studyFolders'], updated);
            }
            return { previousFolders };
        },
        onError: (_err, _vars, context) => {
            // Rollback on error
            if (context?.previousFolders) {
                queryClient.setQueryData(['studyFolders'], context.previousFolders);
            }
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: ['studyFolders'] });
        },
    });
}
export function useAddStudiesToFolder() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ folderId, request }) => addStudiesToFolder(folderId, request),
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: ['studyFolders'] });
            // Invalidate study-specific folder queries for each study added
            variables.request.studyIds.forEach((id) => {
                queryClient.invalidateQueries({ queryKey: ['studyFolders', 'forStudy', id] });
            });
        },
    });
}
export function useRemoveStudyFromFolder() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ folderId, studyId }) => removeStudyFromFolder(folderId, studyId),
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: ['studyFolders'] });
            queryClient.invalidateQueries({ queryKey: ['studyFolders', 'forStudy', variables.studyId] });
        },
    });
}
export function useFoldersForStudy(studyId) {
    return useQuery({
        queryKey: ['studyFolders', 'forStudy', studyId],
        queryFn: () => getFoldersForStudy(studyId),
        enabled: !!studyId,
        staleTime: 30000,
    });
}
